import { useMemo, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import { AlertTriangle } from 'lucide-react'
import { useLeads } from '@/features/leads/use-leads'
import { useLimitsPlan } from '@/hooks/use-plan'
import { maxLeadsPerCampaignFor, planDisplayName, campaignCapMessage } from '@dealcloser/shared'
import { useAddLeadsToCampaign, useCampaigns } from './use-campaigns'
import type { Lead } from '@/features/leads/types'

type AddLeadsDialogProps = {
  readonly campaignId: string
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
}

function getLeadDisplayName(lead: Lead): string {
  if (lead.full_name) return lead.full_name
  const parts = [lead.first_name, lead.last_name].filter(Boolean)
  return parts.length > 0 ? parts.join(' ') : 'Unnamed Lead'
}

export function AddLeadsDialog({
  campaignId,
  open,
  onOpenChange,
}: AddLeadsDialogProps) {
  const [selected, setSelected] = useState<Set<string>>(new Set())

  const { data: allLeads, isLoading: leadsLoading } = useLeads()
  const { data: allCampaigns } = useCampaigns()
  const addLeads = useAddLeadsToCampaign()

  const campaignsById = useMemo(() => {
    const map = new Map<string, string>()
    for (const c of allCampaigns ?? []) map.set(c.id, c.name)
    return map
  }, [allCampaigns])

  // Show every lead EXCEPT those already in THIS campaign. Leads currently
  // assigned to a different campaign stay visible but get a warning badge so
  // the user knows they'd be moving the lead from one campaign to another.
  const availableLeads = useMemo(
    () => (allLeads ?? []).filter((lead) => lead.campaign_id !== campaignId),
    [allLeads, campaignId],
  )

  // Per-plan campaign cap (Basic 30 / Pro 60; comp + agency unlimited).
  // Remaining = cap minus the leads already in this campaign. The backend
  // enforces this too — clamping here just keeps the selection from exceeding
  // what the server would accept, so the cap uses the same comp-aware limits
  // plan the server resolves (resolveLimitsPlan parity).
  const plan = useLimitsPlan()
  const perCampaignCap = maxLeadsPerCampaignFor(plan)
  const currentCount =
    (allCampaigns ?? []).find((cmp) => cmp.id === campaignId)?.leads_count ?? 0
  const remaining = Number.isFinite(perCampaignCap)
    ? Math.max(0, perCampaignCap - currentCount)
    : Number.POSITIVE_INFINITY
  const atCapacity = remaining <= 0

  function toggleLead(leadId: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(leadId)) {
        next.delete(leadId)
      } else {
        // Respect the cap — once we've picked as many as remain, ignore adds.
        if (Number.isFinite(remaining) && next.size >= remaining) return prev
        next.add(leadId)
      }
      return next
    })
  }

  function toggleAll() {
    const maxSelectable = Number.isFinite(remaining)
      ? Math.min(availableLeads.length, remaining)
      : availableLeads.length
    if (selected.size >= maxSelectable) {
      setSelected(new Set())
    } else {
      setSelected(new Set(availableLeads.slice(0, maxSelectable).map((l) => l.id)))
    }
  }

  function handleAdd() {
    if (selected.size === 0) return
    addLeads.mutate(
      { campaignId, leadIds: Array.from(selected) },
      {
        onSuccess: () => {
          setSelected(new Set())
          onOpenChange(false)
        },
      },
    )
  }

  const hasAvailable = availableLeads.length > 0
  const crossCampaignSelected = Array.from(selected).filter((id) => {
    const lead = availableLeads.find((l) => l.id === id)
    return lead && lead.campaign_id && lead.campaign_id !== campaignId
  }).length

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg overflow-hidden">
        <DialogHeader className="min-w-0">
          <DialogTitle>Add Leads to Campaign</DialogTitle>
        </DialogHeader>

        {Number.isFinite(perCampaignCap) && (
          <p className="text-xs text-muted-foreground -mt-1">
            {currentCount} / {perCampaignCap} leads in this campaign
            {atCapacity
              ? `, full on the ${planDisplayName(plan)} plan`
              : `, ${remaining} slot${remaining === 1 ? '' : 's'} left`}
          </p>
        )}

        {atCapacity && (
          <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 dark:border-amber-900/40 dark:bg-amber-950/30 px-3 py-2 text-xs text-amber-900 dark:text-amber-200">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            <span>{campaignCapMessage(plan)}</span>
          </div>
        )}

        {addLeads.isError && (
          <div className="rounded-md border border-red-200 bg-red-50 dark:border-red-900/40 dark:bg-red-950/30 px-3 py-2 text-xs text-red-700 dark:text-red-300">
            {addLeads.error instanceof Error ? addLeads.error.message : 'Failed to add leads.'}
          </div>
        )}

        <div className="py-4 min-w-0 overflow-hidden">
          {leadsLoading && (
            <p className="text-sm text-muted-foreground">Loading leads...</p>
          )}

          {!leadsLoading && !hasAvailable && (
            <p className="text-sm text-muted-foreground">
              No leads available. Every lead in your database is already in this campaign.
            </p>
          )}

          {!leadsLoading && hasAvailable && (
            <div className="flex flex-col gap-2 min-w-0">
              <div className="flex items-center gap-2 pb-2 border-b">
                <Checkbox
                  checked={
                    selected.size === availableLeads.length &&
                    availableLeads.length > 0
                  }
                  onCheckedChange={toggleAll}
                  aria-label="Select all leads"
                />
                <span className="text-xs font-medium text-muted-foreground">
                  {selected.size > 0
                    ? `${selected.size} selected${Number.isFinite(remaining) ? ` (max ${remaining})` : ''}`
                    : `${availableLeads.length} available`}
                </span>
              </div>

              {crossCampaignSelected > 0 && (
                <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 dark:border-amber-900/40 dark:bg-amber-950/30 px-3 py-2 text-xs text-amber-900 dark:text-amber-200">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                  <span>
                    {crossCampaignSelected === 1
                      ? '1 selected lead is already in another campaign and will be moved here.'
                      : `${crossCampaignSelected} selected leads are already in other campaigns and will be moved here.`}
                  </span>
                </div>
              )}

              <div className="max-h-72 overflow-y-auto flex flex-col gap-0.5">
                {availableLeads.map((lead) => {
                  const otherCampaignName =
                    lead.campaign_id && lead.campaign_id !== campaignId
                      ? campaignsById.get(lead.campaign_id) ?? 'another campaign'
                      : null

                  return (
                    <label
                      key={lead.id}
                      className="flex items-start gap-3 rounded-md border bg-muted/30 px-3 py-2 hover:bg-muted cursor-pointer"
                    >
                      <Checkbox
                        checked={selected.has(lead.id)}
                        onCheckedChange={() => toggleLead(lead.id)}
                        aria-label={`Select ${getLeadDisplayName(lead)}`}
                        className="shrink-0 mt-0.5"
                      />
                      <div className="flex flex-col min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium">
                            {getLeadDisplayName(lead)}
                          </span>
                          {otherCampaignName && (
                            <Badge
                              variant="outline"
                              className="text-[10px] px-1.5 py-0 h-4 border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-200"
                            >
                              In: {otherCampaignName}
                            </Badge>
                          )}
                        </div>
                        <span className="text-xs text-muted-foreground break-words">
                          {[lead.job_title, lead.company]
                            .filter(Boolean)
                            .join(' at ')}
                        </span>
                      </div>
                    </label>
                  )
                })}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={addLeads.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAdd}
            disabled={addLeads.isPending || selected.size === 0 || atCapacity}
          >
            {addLeads.isPending
              ? 'Adding...'
              : `Add ${selected.size} Lead${selected.size === 1 ? '' : 's'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
